import axios from 'axios';
import { createAPIClient } from '@/utils/network-utils/createAPIClient';

import { DTO } from '@/type-utils';
import { NotImplementedError } from '@/utils/errors';

import ServerAccountService from '@/services/serverless/ServerAccountService';
import { InvalidCurrentPasswordError } from '@/services/serverless/ServerAccountService/errors/InvalidCurrentPasswordError';
import type { AuthPaymentAndSaveToAccountWalletParams } from '@/services/serverless/ServerAccountService/AuthPaymentAndSaveToAccountWalletParams';
import type { IAddress, ISavedAddress } from '@/services/models/Address';
import type { IAuthenticatedUserTokens } from '@/services/models/User/IUserTokens';
import Service from '../../Service';
import {
  AccountModel,
  IAccount,
  IProfile,
  ProfileModel
} from '../../models/Account';

import { EnvironmentService } from '../EnvironmentService';

import AccountServiceMock from './AccountServiceMock';
import CSRFTokenService from '../CSRFTokenService';
import type { IChangePasswordResult } from './IChangePasswordResult';

/** Abstracts {@link IAccount Account}-related operations. */
export class AccountService extends Service {
  private client = createAPIClient({ baseURL: '/api/account' });

  /**
   * Updates a customer account with the data existing in the provided
   * {@link IAccount} object.
   *
   * @param _account - An account with updated information.
   * @returns A promise that resolves when the account is updated.
   * @throws An {@link UnableToUpdateUserAccountError} when the account could
   * not be updated. See the `cause` property for more details about what
   * specifically went wrong.
   */
  public async updateAccount(_account: IAccount): Promise<void> {
    throw new NotImplementedError(
      'Updating whole accounts at once has not been implemented yet.'
    );
  }

  /**
   * Updates the provided customer {@link IProfile profile}.
   *
   * NOTE: This will only update the Profile. The rest of the Account will be
   * unaffected.
   *
   * @param profile - An {@link IProfile} object with the new profile info.
   * @param tokens - The authenticated user's tokens.
   * @returns A promise that resolves when the profile is updated.
   */
  public async updateProfile(
    profile: IProfile,
    tokens: IAuthenticatedUserTokens
  ): Promise<void> {
    if ((typeof window === "undefined")) {
      await ServerAccountService.updateProfile(profile, tokens);
      return;
    }

    const { accountID } = profile;
    const csrfHeaders = CSRFTokenService.getHeaders();

    // Only transfer the accessToken needed for this operation.
    await this.client.put(
      `${accountID}/profile`,
      { profile: ProfileModel.toDTO(profile), accessToken: tokens.accessToken },
      { headers: csrfHeaders }
    );
  }

  /**
   * Reset user's password.
   *
   * The main difference between `forgotPasswordReset` and `changePassword` is
   * that the user doesn't need to be authenticated to change password. They
   * only need to specify the confirmation code and a new password.
   *
   * @param requestToken - A unique request token (directly related to the
   * submitted email address) that gets generated by the backend and then
   * appended to a password reset url sent to the user's email. This
   * authenticates the user and allows them to change their password.
   * @param newPassword - The new password.
   * @param confirmationCode - The confirmation code used for authentication.
   *
   * @returns A promise that resolves when the password is reset.
   */
  public async forgotPasswordReset(
    requestToken: string,
    newPassword: string,
    confirmationCode: string
  ): Promise<void> {
    const csrfHeaders = CSRFTokenService.getHeaders();

    await this.client.post(
      `/forgot/reset`,
      { requestToken, newPassword, confirmationCode },
      { headers: csrfHeaders }
    );
  }

  /**
   * Changes user password.
   *
   * @param previousPassword - The previous password.
   * @param newPassword - The new password.
   * @param accountID - The ID of the customer's {@link IAccount Account}.
   * @param tokens - The authenticated user's tokens.
   * @param email - The user's email address (required for auto-login after password change).
   *
   * @returns A promise that resolves with the change password result.
   */
  public async changePassword(
    previousPassword: string,
    newPassword: string,
    accountID: string,
    tokens: IAuthenticatedUserTokens,
    email: string
  ): Promise<IChangePasswordResult> {
    if ((typeof window === "undefined")) {
      await ServerAccountService.changePassword(
        newPassword,
        previousPassword,
        accountID,
        tokens
      );

      // Server-side calls don't have access to the config-based response.
      // Default to requiring relogin for safety.
      return { requiresRelogin: true, autoLogin: false };
    }

    const csrfHeaders = CSRFTokenService.getHeaders();

    try {
      // Include email for auto-login after password change.
      // The server re-authenticates with the new password if enabled.
      const response = await this.client.put<{
        requiresRelogin: boolean;
        autoLogin: boolean;
      }>(
        `${accountID}/change-password`,
        {
          previousPassword,
          newPassword,
          accessToken: tokens.accessToken,
          email
        },
        { headers: csrfHeaders }
      );

      return {
        requiresRelogin: response.data.requiresRelogin,
        autoLogin: response.data.autoLogin
      };
    } catch (error) {
      // Translate API error codes to domain errors.
      // The API wraps error details in a `data` property, so the code is at
      // error.response.data.data.code
      if (
        axios.isAxiosError(error) &&
        (error.response?.data as { data?: { code?: string } })?.data?.code ===
          'INVALID_CURRENT_PASSWORD'
      ) {
        throw new InvalidCurrentPasswordError(
          'The provided current password is incorrect.',
          { cause: error }
        );
      }

      throw error;
    }
  }

  /**
   * Initiates a password reset flow and sends a verification code to the
   * user's email address.
   *
   * @param email - The user's email address.
   * @returns A promise that resolves when the reset email is sent.
   */
  public async forgotPassword(email: string): Promise<void> {
    const csrfHeaders = CSRFTokenService.getHeaders();

    await this.client.post(
      `/forgot/request`,
      { email },
      { headers: csrfHeaders }
    );
  }

  /**
   * Retrieves a User's account.
   *
   * @param accountID - The user's identifier.
   * @param tokens - The authenticated user's tokens.
   *
   * @returns The user's account in {@link AccountModel} form.
   */
  public async getUserAccount(
    accountID: string,
    tokens: IAuthenticatedUserTokens
  ): Promise<AccountModel> {
    if ((typeof window === "undefined")) {
      const dto = await ServerAccountService.getUserAccount(accountID, tokens);

      return AccountModel.from(dto);
    }

    const csrfHeaders = CSRFTokenService.getHeaders();

    // Only transfer the accessToken needed for this operation.
    const res = await this.client.post<DTO<IAccount>>(
      `/${accountID}`,
      { accessToken: tokens.accessToken },
      { headers: csrfHeaders }
    );

    return AccountModel.from(res.data);
  }

  /**
   * Deletes personal information.
   *
   * @param accountID - The ID of the customer's {@link IAccount Account}.
   * @param tokens - The authenticated user's tokens containing accessToken and
   * refreshToken.
   * @throws An {@link UnableToDeleteAccountError} when the account could not be
   * deleted.
   */
  public async deleteAccount(
    accountID: string,
    tokens: IAuthenticatedUserTokens
  ): Promise<void> {
    if ((typeof window === "undefined")) {
      await ServerAccountService.deleteAccount(accountID, tokens);
      return;
    }

    const csrfHeaders = CSRFTokenService.getHeaders();

    // Only transfer the tokens needed for this operation.
    await this.client.post(
      `${accountID}/delete`,
      { accessToken: tokens.accessToken, refreshToken: tokens.refreshToken },
      { headers: csrfHeaders }
    );
  }

  // #region Wallet related methods

  /**
   * Sets the preferred payment method for a user account profile.
   *
   * @param paymentInstrumentID - The ID of the payment instrument to set as
   * preferred.
   * @param accountID - The ID of the customer's {@link IAccount Account}.
   * @param tokens - The authenticated user's tokens.
   * @returns A promise that resolves when the preferred payment is set.
   */
  public async setPreferredPayment(
    paymentInstrumentID: string,
    accountID: string,
    tokens: IAuthenticatedUserTokens
  ): Promise<void> {
    if ((typeof window === "undefined")) {
      return ServerAccountService.setPreferredPayment(
        paymentInstrumentID,
        accountID,
        tokens
      );
    }

    const csrfHeaders = CSRFTokenService.getHeaders();

    // Only transfer the accessToken needed for this operation.
    return this.client.post(
      `${accountID}/wallet/${paymentInstrumentID}/set-preferred`,
      { accessToken: tokens.accessToken },
      { headers: { ...csrfHeaders } }
    );
  }

  /**
   * Saves a payment to the account wallet.
   *
   * For more details on the orchestration of this process, see
   * {@link ServerAccountService.authPaymentAndSaveToAccountWallet}.
   *
   * @param accountID - The ID of the customer's {@link IAccount Account}.
   * @param tokens - The authenticated user's tokens.
   * @param payment - The parameters for saving a payment to the account wallet.
   * @returns The prepared credit card with a token.
   * @throws An {@link UnableToUpdateAccountError} when the credit card payment could
   * not be prepared for authorization.
   */
  public async authPaymentAndSaveToAccountWallet(
    accountID: string,
    tokens: IAuthenticatedUserTokens,
    payment: AuthPaymentAndSaveToAccountWalletParams
  ): Promise<void> {
    if ((typeof window === "undefined")) {
      return ServerAccountService.authPaymentAndSaveToAccountWallet(
        accountID,
        tokens,
        payment
      );
    }

    const csrfHeaders = CSRFTokenService.getHeaders();

    // Only transfer the accessToken needed for this operation.
    return this.client.post(
      `${accountID}/wallet`,
      { payment, accessToken: tokens.accessToken },
      { headers: { ...csrfHeaders } }
    );
  }

  /**
   * Deletes a saved payment from the user's wallet.
   *
   * @param paymentInstrumentID - The ID of the payment instrument to delete
   * from the wallet.
   * @param accountID - The ID of the customer's {@link IAccount Account}.
   * @param tokens - The authenticated user's tokens.
   * @returns A promise that resolves when the payment instrument is deleted.
   */
  public async deleteSavedPaymentFromWallet(
    paymentInstrumentID: string,
    accountID: string,
    tokens: IAuthenticatedUserTokens
  ): Promise<void> {
    if ((typeof window === "undefined")) {
      return ServerAccountService.deleteSavedPaymentFromWallet(
        paymentInstrumentID,
        accountID,
        tokens
      );
    }

    const csrfHeaders = CSRFTokenService.getHeaders();

    // Only transfer the accessToken needed for this operation.
    return this.client.delete(`${accountID}/wallet/${paymentInstrumentID}`, {
      headers: { ...csrfHeaders },
      data: { accessToken: tokens.accessToken }
    });
  }

  // #endregion Wallet related methods

  // #region Saved Address Methods

  /**
   * Retrieves the saved addresses for a user account.
   *
   * @param accountID - The ID of the customer's {@link IAccount Account}.
   * @param tokens - The authenticated user's tokens.
   *
   * @returns An array of saved addresses.
   */
  public async getSavedAddresses(
    accountID: string,
    tokens: IAuthenticatedUserTokens
  ): Promise<Array<ISavedAddress>> {
    if ((typeof window === "undefined")) {
      const addresses = await ServerAccountService.getSavedAddresses(
        accountID,
        tokens
      );

      return addresses;
    }

    const csrfHeaders = CSRFTokenService.getHeaders();

    // Only transfer the accessToken needed for this operation.
    const res = await this.client.post<IAccount['savedAddresses']>(
      `/${accountID}/saved-addresses`,
      { accessToken: tokens.accessToken },
      { headers: csrfHeaders }
    );

    return res.data;
  }

  /**
   * Creates a new saved address for a user account.
   *
   * @param address - The address to save.
   * @param accountID - The ID of the customer's {@link IAccount Account}.
   * @param tokens - The authenticated user's tokens.
   * @param setAsPreferred - Whether the address should be set as preferred.
   * @param nickName - An optional nickname for the saved address.
   *
   * @returns The updated list of saved addresses.
   */
  public async createSavedAddress(
    address: IAddress,
    accountID: string,
    tokens: IAuthenticatedUserTokens,
    setAsPreferred: boolean,
    nickName?: string
  ): Promise<Array<ISavedAddress>> {
    if ((typeof window === "undefined")) {
      const addresses = await ServerAccountService.createSavedAddress(
        address,
        accountID,
        tokens,
        setAsPreferred,
        nickName
      );

      return addresses;
    }

    const csrfHeaders = CSRFTokenService.getHeaders();

    // Only transfer the accessToken needed for this operation.
    const { data } = await this.client.put<Array<ISavedAddress>>(
      `/${accountID}/saved-addresses`,
      { address, nickName, accessToken: tokens.accessToken, setAsPreferred },
      { headers: csrfHeaders }
    );

    return data;
  }

  /**
   * Deletes a saved address for a user account.
   *
   * @param addressID - The ID of the saved address to delete.
   * @param accountID - The ID of the customer's {@link IAccount Account}.
   * @param tokens - The authenticated user's tokens.
   * @returns A promise that resolves when the address is deleted.
   */
  public async deleteSavedAddress(
    addressID: string,
    accountID: string,
    tokens: IAuthenticatedUserTokens
  ): Promise<void> {
    if ((typeof window === "undefined")) {
      await ServerAccountService.deleteSavedAddress(
        addressID,
        accountID,
        tokens
      );
      return;
    }
    const csrfHeaders = CSRFTokenService.getHeaders();

    // Only transfer the accessToken needed for this operation.
    await this.client.delete(`/${accountID}/saved-addresses/${addressID}`, {
      headers: csrfHeaders,
      data: { accessToken: tokens.accessToken }
    });
  }

  /**
   * Updates a saved address for a user account.
   *
   * @param address - The updated saved address.
   * @param accountID - The ID of the customer's {@link IAccount Account}.
   * @param tokens - The authenticated user's tokens.
   * @param setAsPreferred - Whether the address should be set as preferred.
   * @returns The updated list of saved addresses.
   */
  public async updateSavedAddress(
    address: ISavedAddress,
    accountID: string,
    tokens: IAuthenticatedUserTokens,
    setAsPreferred: boolean
  ): Promise<Array<ISavedAddress>> {
    if ((typeof window === "undefined")) {
      const addresses = await ServerAccountService.updateSavedAddress(
        address,
        accountID,
        tokens,
        setAsPreferred
      );
      return addresses;
    }

    const csrfHeaders = CSRFTokenService.getHeaders();
    // Only transfer the accessToken needed for this operation.
    const res = await this.client.patch<Array<ISavedAddress>>(
      `/${accountID}/saved-addresses`,
      { address, accessToken: tokens.accessToken, setAsPreferred },
      { headers: csrfHeaders }
    );

    return res.data;
  }

  /**
   * Sets the preferred saved address for a user account.
   *
   * @param addressID - The ID of the saved address to set as preferred.
   * @param accountID - The ID of the customer's {@link IAccount Account}.
   * @param tokens - The authenticated user's tokens.
   * @returns A promise that resolves when the preferred address is set.
   */
  public async setPreferredSavedAddress(
    addressID: string,
    accountID: string,
    tokens: IAuthenticatedUserTokens
  ): Promise<void> {
    if ((typeof window === "undefined")) {
      await ServerAccountService.setPreferredAddress(
        addressID,
        accountID,
        tokens
      );
      return;
    }
    const csrfHeaders = CSRFTokenService.getHeaders();
    // Only transfer the accessToken needed for this operation.
    await this.client.post(
      `/${accountID}/saved-addresses/${addressID}/set-preferred`,
      { accessToken: tokens.accessToken },
      { headers: csrfHeaders }
    );
  }

  // #endregion
}

export default AccountService.withMock(
  new AccountServiceMock(AccountService)
) as unknown as AccountService;
